import React from 'react';
import '../Pipeline.css';

/**
 * AgentActivityGraph
 *
 * Shows a network graph of the two-agent pipeline:
 *   [ Creator ] ──→ [ Validator ] ──→ [ Result ]
 *
 * Each node pulses while its status is "working" / "validating".
 * Activity log entries from the backend drive the visual state.
 */

const STATUS_COLOR = {
  working:    '#3b82f6',   // blue
  retrying:   '#f59e0b',   // amber
  done:       '#10b981',   // green
  validating: '#8b5cf6',   // violet
  passed:     '#10b981',   // green
  failed:     '#ef4444',   // red
  idle:       '#cbd5e1',   // slate
};

const STATUS_LABEL = {
  working:    'Working…',
  retrying:   'Retrying…',
  done:       'Done',
  validating: 'Validating…',
  passed:     'Approved ✓',
  failed:     'Failed ✗',
  idle:       'Idle',
};

function AgentNode({ name, icon, status, message, isActive }) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.idle;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 110 }}>
      <div
        style={{
          width: 64, height: 64,
          borderRadius: '50%',
          background: `${color}18`,
          border: `2.5px solid ${color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.6rem',
          boxShadow: isActive ? `0 0 0 6px ${color}30, 0 0 0 12px ${color}10` : 'none',
          animation: isActive ? 'agentPulse 1.4s ease-in-out infinite' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        {icon}
      </div>
      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a' }}>{name}</div>
      <div
        style={{
          fontSize: '0.72rem',
          color: color,
          fontWeight: 500,
          background: `${color}15`,
          padding: '2px 8px',
          borderRadius: 20,
          textAlign: 'center',
        }}
      >
        {STATUS_LABEL[status] || status}
      </div>
      {message && (
        <div style={{ fontSize: '0.7rem', color: '#64748b', maxWidth: 110, textAlign: 'center', lineHeight: 1.3 }}>
          {message}
        </div>
      )}
    </div>
  );
}

function Arrow({ active, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '0 8px', marginTop: -10 }}>
      <div
        style={{
          height: 2,
          width: 50,
          background: active ? color : '#e2e8f0',
          borderRadius: 2,
          transition: 'background 0.4s',
          position: 'relative',
        }}
      />
      <div
        style={{
          width: 0, height: 0,
          borderTop: '5px solid transparent',
          borderBottom: '5px solid transparent',
          borderLeft: `7px solid ${active ? color : '#e2e8f0'}`,
          marginTop: -14,
          marginLeft: 50,
          transition: 'border-left-color 0.4s',
        }}
      />
    </div>
  );
}

function AgentActivityGraph({ activity = [], polling = false }) {
  // Derive current node states from the activity log
  const lastByAgent = {};
  activity.forEach((entry) => {
    lastByAgent[entry.agent] = entry;
  });

  const creatorEntry    = lastByAgent['Creator'];
  const validatorEntry  = lastByAgent['Validator'];

  const creatorStatus   = creatorEntry?.status  || (polling ? 'working' : 'idle');
  const validatorStatus = validatorEntry?.status || 'idle';

  const creatorActive   = ['working', 'retrying'].includes(creatorStatus);
  const validatorActive = ['validating'].includes(validatorStatus);
  const resultActive    = ['passed'].includes(validatorStatus);

  const arrowColor = STATUS_COLOR.working;

  // Build readable activity log
  const log = [...activity].reverse().slice(0, 8);

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      padding: '16px 20px',
      marginBottom: 16,
    }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        🔄 Agent Pipeline
      </div>

      {/* Network graph */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexWrap: 'wrap' }}>
        <AgentNode
          name="Creator"
          icon="🤖"
          status={creatorStatus}
          message={creatorEntry?.message}
          isActive={creatorActive}
        />
        <Arrow active={creatorActive || validatorStatus !== 'idle'} color={arrowColor} />
        <AgentNode
          name="Validator"
          icon="🔍"
          status={validatorStatus}
          message={validatorEntry?.message}
          isActive={validatorActive}
        />
        <Arrow active={resultActive} color={STATUS_COLOR.passed} />
        <AgentNode
          name="Output"
          icon={resultActive ? '✅' : '⏳'}
          status={resultActive ? 'passed' : (polling ? 'idle' : 'idle')}
          isActive={resultActive}
        />
      </div>

      {/* Activity log */}
      {log.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Activity Log
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {log.map((entry, i) => {
              const color = STATUS_COLOR[entry.status] || '#64748b';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.75rem' }}>
                  <span style={{ color, fontWeight: 700, minWidth: 70 }}>[{entry.agent}]</span>
                  <span style={{ color: '#475569' }}>{entry.message || entry.status}</span>
                  <span style={{ color: '#cbd5e1', marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                    {entry.ts ? new Date(entry.ts).toLocaleTimeString() : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes agentPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(59,130,246,0.2); }
          50%       { box-shadow: 0 0 0 10px rgba(59,130,246,0.08); }
        }
      `}</style>
    </div>
  );
}

export default AgentActivityGraph;
